import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircle, Edit, Layers } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import { useGetProductsQuery } from "../productsApi";
import { useCanCreateProducts } from "../useProductPermissions";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "", label: "All Statuses" },
];

// Landing page for the product master (texas_master/GUI/Master/Frm_Product_Details.cs),
// against the live /api/v1/masters/products endpoint (handoff §9.8) - same
// branch-wise pattern as Customers (§9.7): search/isActive/branchId filter,
// no delete endpoint (isActive toggle only), Product_Code collides across
// branches so every row's edit link carries both branchId and productCode.
const ProductsList = () => {
  const canCreate = useCanCreateProducts();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const { selectedBranchId } = useBranchFilter();

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data: products = [], isLoading } = useGetProductsQuery({
    search: search || undefined,
    isActive: status === "" ? undefined : status === "active",
    branchId: selectedBranchId ?? undefined,
  });

  const columns = [
    {
      title: "Code",
      dataIndex: "productCodeNew",
      width: 110,
      render: (value, record) => (
        <Link to={paths.productEdit(record.branchId, record.productCode)}>{value}</Link>
      ),
    },
    {
      title: "Name",
      dataIndex: "productName",
      width: 260,
      ellipsis: { showTitle: true },
      render: (value, record) => (
        <>
          {value || "-"}
          {record.isMapped && (
            <span className="badge bg-info-transparent ms-2" title={`Home branch: ${record.branchCode}`}>
              Shared from {record.branchCode}
            </span>
          )}
        </>
      ),
    },
    { title: "Group", dataIndex: "productGroupName", render: (v) => v || "-" },
    { title: "Branch", dataIndex: "branchCode" },
    { title: "Sales Rate", dataIndex: "salesRate" },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (value) => (
        <span className={`badge ${value ? "badge-linesuccess" : "badge-linedanger"}`}>
          {value ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="edit-delete-action">
          <button
            type="button"
            className="btn btn-link p-2"
            title="Edit"
            onClick={() => navigate(paths.productEdit(record.branchId, record.productCode))}
          >
            <Edit size={16} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Products</h4>
              <h6>Inventory item master</h6>
            </div>
          </div>
          <div className="page-btn d-flex gap-2">
            <button type="button" className="btn btn-outline-primary" onClick={() => navigate(paths.productGroups)}>
              <Layers className="me-2" size={16} />
              Product Groups
            </button>
            {canCreate && (
              <button type="button" className="btn btn-added" onClick={() => navigate(paths.productNew)}>
                <PlusCircle className="me-2" size={16} />
                Add New Product
              </button>
            )}
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="table-top">
              <div className="search-set">
                <div className="search-input">
                  <input
                    type="text"
                    placeholder="Search product name or code"
                    className="form-control form-control-sm formsearch"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                </div>
              </div>
              <div className="form-sort">
                <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="table-responsive">
              <DataTable
                columns={columns}
                dataSource={products}
                rowKey={(record) => `${record.branchId}-${record.productCode}`}
                loading={isLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsList;
